import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight, Check, X } from "lucide-react";
import { Reveal } from "../Reveal";
import { SERVICES } from "../../lib/data";

function ServiceCard({ s, onOpen }) {
  return (
    <motion.button
      data-testid={`service-card-${s.id}`}
      onClick={() => onOpen(s)}
      whileHover="hover"
      className="group relative overflow-hidden rounded-3xl border border-ink-soft bg-ink-surface p-8 text-left transition-all duration-300 hover:border-gold/40 hover:-translate-y-2"
    >
      <div className="pointer-events-none absolute -right-10 -top-10 h-40 w-40 rounded-full bg-gold/10 opacity-0 blur-3xl transition-opacity duration-500 group-hover:opacity-100" />
      <div className="flex items-start justify-between">
        <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-ink text-gold transition-colors group-hover:bg-gold group-hover:text-ink">
          <s.icon className="h-6 w-6" strokeWidth={1.5} />
        </div>
        <span className="rounded-full border border-white/10 px-3 py-1 text-xs text-white/50">{s.tag}</span>
      </div>
      <h3 className="mt-6 font-display text-2xl font-medium tracking-tight">{s.title}</h3>
      <p className="mt-3 text-sm leading-relaxed text-white/55">{s.desc}</p>
      <span className="mt-6 inline-flex items-center gap-1 text-sm font-medium text-gold">
        View details <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" strokeWidth={1.5} />
      </span>
    </motion.button>
  );
}

export default function Services() {
  const [active, setActive] = useState(null);

  return (
    <section id="services" data-testid="services-section" className="relative bg-ink py-24 md:py-32">
      <div className="mx-auto max-w-7xl px-6 md:px-10">
        <div className="flex flex-col items-start justify-between gap-6 md:flex-row md:items-end">
          <div>
            <Reveal><span className="text-xs font-bold uppercase tracking-[0.3em] text-gold">What We Do</span></Reveal>
            <Reveal delay={0.1}>
              <h2 className="mt-5 max-w-2xl font-display text-4xl font-medium leading-tight tracking-tight sm:text-5xl lg:text-6xl">
                Premium service for every engine.
              </h2>
            </Reveal>
          </div>
          <Reveal delay={0.2}>
            <p className="max-w-sm text-white/50">From daily commuters to diesel trucks and full fleets — we bring the right oil to your door.</p>
          </Reveal>
        </div>

        <div className="mt-16 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
          {SERVICES.map((s, i) => (
            <Reveal key={s.id} delay={(i % 3) * 0.1}>
              <ServiceCard s={s} onOpen={setActive} />
            </Reveal>
          ))}
        </div>
      </div>

      <AnimatePresence>
        {active && (
          <motion.div
            className="fixed inset-0 z-[90] flex items-center justify-center p-4"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            data-testid="service-modal"
          >
            <div className="absolute inset-0 bg-ink/80 backdrop-blur-sm" onClick={() => setActive(null)} />
            <motion.div
              initial={{ scale: 0.92, y: 30, opacity: 0 }}
              animate={{ scale: 1, y: 0, opacity: 1 }}
              exit={{ scale: 0.92, y: 30, opacity: 0 }}
              transition={{ type: "spring", stiffness: 260, damping: 26 }}
              className="relative w-full max-w-lg overflow-hidden rounded-3xl border border-white/10 bg-ink-surface p-8"
            >
              <button data-testid="service-modal-close" onClick={() => setActive(null)} className="absolute right-5 top-5 text-white/50 hover:text-white">
                <X className="h-5 w-5" />
              </button>
              <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-gold text-ink">
                <active.icon className="h-7 w-7" strokeWidth={1.5} />
              </div>
              <span className="mt-5 inline-block rounded-full border border-white/10 px-3 py-1 text-xs text-white/50">{active.tag}</span>
              <h3 className="mt-3 font-display text-3xl font-medium tracking-tight">{active.title}</h3>
              <p className="mt-3 text-white/60">{active.desc}</p>
              <ul className="mt-6 space-y-3">
                {active.points.map((p) => (
                  <li key={p} className="flex items-center gap-3 text-white/80">
                    <span className="flex h-6 w-6 items-center justify-center rounded-full bg-gold/15 text-gold"><Check className="h-3.5 w-3.5" /></span>
                    {p}
                  </li>
                ))}
              </ul>
              <button
                data-testid="service-modal-book"
                onClick={() => { setActive(null); document.querySelector("#contact")?.scrollIntoView({ behavior: "smooth" }); }}
                className="mt-8 w-full rounded-full bg-gold py-3.5 font-semibold text-ink transition-colors hover:bg-gold-hover"
              >
                Book This Service
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
